import type { Attestation } from "@allergy123/contracts/clinical";
import { Button, Card, Status } from "@allergy123/ui";
import { useState } from "react";
import { useClinical } from "../api";
import { ATTESTATIONS, PROVIDER_KINDS } from "../lib/attestations";
import { errorText } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { useSession } from "../session";
import { SignaturePad } from "./SignaturePad";

/** One required signature: the statement, who signs, the pad, and the immutable record once it exists. */
export function SignatureCard({ kind, subjectType, subjectId, existing, timezone, disabled = false, disabledReason, onSigned }: { kind: string; subjectType: "encounter" | "treatment_order"; subjectId: string; existing?: Attestation; timezone: string; disabled?: boolean; disabledReason?: string; onSigned: () => void }) {
  const api = useClinical();
  const s = useSession();
  const copy = ATTESTATIONS[kind] ?? { label: kind, signer: "patient" as const, statement: "" };
  const statement = s.statementFor(kind);
  const [png, setPng] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsOrder = PROVIDER_KINDS.has(kind) && !s.can("clinical:order");

  async function sign() {
    if (!png) return;
    setBusy(true);
    setError(null);
    try {
      await api.attestations.create({ subjectType, subjectId, kind: kind as Attestation["kind"], statementVersion: statement.version, image: png });
      setPng(null);
      onSigned();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby={`sig-${kind}`} dense>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={`sig-${kind}`} className="text-h3 font-semibold text-anchor">
          {copy.label}
        </h3>
        <span className="text-small text-ink-secondary">Signed by the {copy.signer}</span>
      </div>
      <p className="mb-4 max-w-[68ch] text-ink">{statement.text}</p>
      {existing ? (
        <Status kind="success" label={`Signed ${formatDateTime(existing.signedAt, timezone)} · recorded by ${existing.actorName}`} />
      ) : needsOrder ? (
        <Status kind="info" label="A provider with ordering permission signs this." />
      ) : disabled ? (
        <Status kind="info" label={disabledReason ?? "Not yet."} />
      ) : (
        <div className="flex flex-col gap-3">
          <SignaturePad label={copy.label} onChange={setPng} />
          {error && <Status kind="error" label={error} />}
          <div>
            <Button onClick={() => void sign()} disabled={!png || busy}>
              Record signature
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
